import React from 'react';
import { type DuroRouteObject } from '~/shared/routing/types';
import SettingsPage from './SettingsPage';
import { GeneralAgencySettingsForm, GeneralAgencySettingsSection } from '~/libraries/agencies/GeneralAgencySettingsForm';
import { MedicareAgencySettingsForm, MedicareAgencySettingsSection } from '~/libraries/agencies/MedicareAgencySettingsForm';
import { DisciplineAgencySettingsForm, DisciplineAgencySettingsSection } from '~/libraries/agencies/DisciplineAgencySettingsForm';
import {
	ComprehensiveAssessmentAgencySettingsForm,
	ComprehensiveAssessmentAgencySettingsSection
} from '~/libraries/agencies/ComprehensiveAssessmentAgencySettingsForm';
import { VisitAgencySettingsForm, VisitAgencySettingsSection } from '~/libraries/agencies/VisitAgencySettingsForm';
import { AgencyStyleSettingsSection, AgenyStyleSettingsForm } from '~/libraries/agencies/AgencyStyleSettingsForm';
import { PharmacyLibrary, PharmacyLibrarySection } from '~/libraries/pharmacies/PharmacyLibrary';
import { AddPharmacyForm, EditPharmacyForm } from '~/libraries/pharmacies/PharmacyForm';
import { PhysicianLibrary, PhysicianLibrarySection } from '~/libraries/physicians/PhysicianLibrary';
import { AddPhysicianForm, EditPhysicianForm } from '~/libraries/physicians/PhysicianForm';
import { InsuranceProviderLibrary, InsuranceProviderLibrarySection } from '~/libraries/insurance-providers/InsuranceProviderLibrary';
import { AddInsuranceProviderForm, EditInsuranceProviderForm } from '~/libraries/insurance-providers/InsuranceProviderForm';
import { PhysicianGroupLibrary, PhysicianGroupLibrarySection } from '~/libraries/physicians/groups/PhysicianGroupLibrary';
import { AddPhysicianGroupForm, EditPhysicianGroupForm } from '~/libraries/physicians/groups/PhysicianGroupForm';
import { FacilityLibrary, FacilityLibrarySection } from '~/libraries/facilities/FacilityLibrary';
import { AddFacilityForm, EditFacilityForm } from '~/libraries/facilities/FacilityForm';
import { UserLibrary, UserLibrarySection } from '~/libraries/users/UserLibrary';
import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { Outlet } from 'react-router-dom';
import { TechnicalAgencySettings, TechnicalAgencySettingsSection } from '~/libraries/agencies/TechnicalAgencySettings';
import { EditUserForms } from '~/libraries/users/EditUserForms';
import { InterventionDefinitionLibrary, InterventionDefinitionLibrarySection } from '~/libraries/interventions/library/InterventionDefinitionLibrary';
import { EditInterventionDefinitionForm } from '~/libraries/interventions/library/EditInterventionDefinitionForm';
import { OasisDefinitionLibrary, OasisDefinitionLibrarySection } from '~/libraries/oasis/OasisDefinitionLibrary';
import { InsuranceProviderDetail } from '~/libraries/insurance-providers/InsuranceProviderDetail';
import { InsuranceProviderPlanDetail } from '~/libraries/insurance-providers/plans/InsuranceProviderPlanDetail';

export const routes: DuroRouteObject[] = [
	{
		path: 'settings',
		handle: {
			menuLabel: 'Settings'
		},
		element: (
			<SuspenseWrapper name="SettingsPage">
				<Outlet />
			</SuspenseWrapper>
		),
		children: [
			{ index: true, element: <SettingsPage />, handle: { menuLabel: 'All Settings', disableBack: true } },
			{
				path: 'agency',
				handle: {
					menuLabel: 'Agency Settings',
					disableBack: true
				},
				children: [
					{
						path: GeneralAgencySettingsSection.settingsView,
						element: <GeneralAgencySettingsForm />,
						handle: { menuLabel: GeneralAgencySettingsSection.title, disableBreadCrumb: true }
					},
					{
						path: MedicareAgencySettingsSection.settingsView,
						element: <MedicareAgencySettingsForm />,
						handle: { menuLabel: MedicareAgencySettingsSection.title }
					},
					{
						path: DisciplineAgencySettingsSection.settingsView,
						element: <DisciplineAgencySettingsForm />,
						handle: { menuLabel: DisciplineAgencySettingsSection.title }
					},
					{
						path: ComprehensiveAssessmentAgencySettingsSection.settingsView,
						element: <ComprehensiveAssessmentAgencySettingsForm />,
						handle: { menuLabel: ComprehensiveAssessmentAgencySettingsSection.title }
					},
					{
						path: VisitAgencySettingsSection.settingsView,
						element: <VisitAgencySettingsForm />,
						handle: { menuLabel: VisitAgencySettingsSection.title }
					},
					{
						path: AgencyStyleSettingsSection.settingsView,
						element: <AgenyStyleSettingsForm />,
						handle: { menuLabel: AgencyStyleSettingsSection.title }
					},
					{
						path: TechnicalAgencySettingsSection.settingsView,
						element: <TechnicalAgencySettings />,
						handle: { menuLabel: TechnicalAgencySettingsSection.title }
					}
				]
			},
			{
				path: 'libraries',
				handle: {
					menuLabel: 'Library Settings',
					disableBack: true
				},
				children: [
					{
						path: FacilityLibrarySection.settingsView,
						handle: { menuLabel: FacilityLibrarySection.title },
						children: [
							{
								index: true,
								element: <FacilityLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditFacilityForm />,
								handle: { menuLabel: 'Edit Facility' }
							},
							{
								path: 'add',
								element: <AddFacilityForm />,
								handle: { menuLabel: 'Add Facility' }
							}
						]
					},
					{
						path: InsuranceProviderLibrarySection.settingsView,
						handle: { menuLabel: InsuranceProviderLibrarySection.title },
						children: [
							{
								index: true,
								element: <InsuranceProviderLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditInsuranceProviderForm />,
								handle: { menuLabel: 'Edit Insurance Provider' }
							},
							{
								path: 'add',
								element: <AddInsuranceProviderForm />,
								handle: { menuLabel: 'Add Insurance Provider' }
							},
							{
								path: 'view/:id',
								element: <InsuranceProviderDetail />,
								handle: { menuLabel: 'View Insurance Provider' }
							},
							{
								path: 'view/:id/plans/:planId',
								element: <InsuranceProviderPlanDetail />,
								handle: { menuLabel: 'Plan Detail' }
							}
						]
					},
					{
						path: PharmacyLibrarySection.settingsView,
						handle: { menuLabel: PharmacyLibrarySection.title },
						children: [
							{
								index: true,
								element: <PharmacyLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditPharmacyForm />,
								handle: { menuLabel: 'Edit Pharmacy' }
							},
							{
								path: 'add',
								element: <AddPharmacyForm />,
								handle: { menuLabel: 'Add Pharmacy' }
							}
						]
					},
					{
						path: PhysicianLibrarySection.settingsView,
						handle: { menuLabel: PhysicianLibrarySection.settingsView },
						children: [
							{
								index: true,
								element: <PhysicianLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditPhysicianForm />,
								handle: { menuLabel: 'Edit Physician' }
							},
							{
								path: 'add',
								element: <AddPhysicianForm />,
								handle: { menuLabel: 'Add Physician' }
							}
						]
					},
					{
						path: PhysicianGroupLibrarySection.settingsView,
						handle: { menuLabel: PhysicianGroupLibrarySection.title },
						children: [
							{
								index: true,
								element: <PhysicianGroupLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditPhysicianGroupForm />,
								handle: { menuLabel: 'Edit Physician Group' }
							},
							{
								path: 'add',
								element: <AddPhysicianGroupForm />,
								handle: { menuLabel: 'Add Physician Group' }
							}
						]
					},
					{
						path: UserLibrarySection.settingsView,
						handle: { menuLabel: UserLibrarySection.title },
						children: [
							{
								index: true,
								element: <UserLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditUserForms />,
								handle: { menuLabel: 'Edit User' }
							}
						]
					},
					{
						path: InterventionDefinitionLibrarySection.settingsView,
						handle: { menuLabel: InterventionDefinitionLibrarySection.title },
						children: [
							{
								index: true,
								element: <InterventionDefinitionLibrary />,
								handle: { menuLabel: 'All' }
							},
							{
								path: 'edit/:id',
								element: <EditInterventionDefinitionForm />,
								handle: { menuLabel: 'Edit Task Definition' }
							}
						]
					},
					{
						path: OasisDefinitionLibrarySection.settingsView,
						handle: { menuLabel: OasisDefinitionLibrarySection.title },
						children: [
							{
								index: true,
								element: <OasisDefinitionLibrary />,
								handle: { menuLabel: 'All' }
							}
						]
					}
				]
			}
		]
	}
];

export function getEditUserRoute(userId: string) {
	return `/settings/libraries/users/edit/${userId}`;
}
